/**
 * The blank-value test for the browser (D-378; SPEC-SERVICE-INTEGRATION
 * RULE-INTEG-031 (a) and (b); SPEC-FRONTEND-RULE-097 (1)).
 *
 * This module is the ONLY client blank test. It decides with the generated
 * Unicode 15.0.0 table in visibleTextTable.ts, which is produced from the same
 * canonical file as the Go and Python tables, so all three runtimes answer the
 * same question the same way. There is deliberately no `\p{...}` regex class,
 * no `\s` and no `.trim()` anywhere in it: those consult the BROWSER's own
 * Unicode data, which on an older engine can disagree with the server and turn
 * a client "this is fine" into a server 400 nobody can explain.
 *
 * Three promises every caller can rely on (RULE-INTEG-031 (a) and (b)):
 *
 * - The empty string is NEVER blank here. `isBlankValue("")` is false, and each
 *   caller keeps whatever meaning it gives "" (inherit, clear, absent). This is
 *   the one point where the helper differs from `isBlankDisclosureText` in
 *   aiDisclosure.ts, for which "" IS blank because a configuration tier reads
 *   an empty override as "inherit from the tier above".
 * - The test never rewrites a value. It answers yes or no; a value that passes
 *   is sent exactly as it was typed.
 * - Nothing refused is echoed or logged. This module logs nothing at all.
 *
 * The table is validated at module load and a malformed one THROWS, so a broken
 * bundle fails at its first import instead of quietly letting every value pass
 * (STORY-VTI-ONE-TEST-AC-005). Nothing falls back to a regex or to trim().
 */
import { BLANK_RANGES, VISIBLE_TEXT_UNICODE_VERSION } from "./visibleTextTable";

/** The Unicode version this helper is written against (RULE-INTEG-031 (a)). */
export const EXPECTED_UNICODE_VERSION = "15.0.0";

/** The highest code point Unicode defines; a range may not exceed it. */
const MAX_CODE_POINT = 0x10ffff;

/**
 * Thrown when the generated table is unusable. It names this module so a
 * bundled stack trace points at the blank test rather than at whichever form
 * happened to import it first.
 */
export class VisibleTextTableError extends Error {
  constructor(message: string) {
    super(`web/src/utils/visibleText.ts: ${message}`);
    this.name = "VisibleTextTableError";
  }
}

/**
 * Validate a generated blank table: non-empty, every range well formed and
 * inside Unicode, strictly sorted and therefore non-overlapping, and pinned to
 * the expected Unicode version.
 *
 * Exported so visibleTextTable.drift.test.ts can hand it a deliberately broken
 * table and see it throw. Production code calls it once, below.
 */
export function assertValidBlankTable(
  ranges: ReadonlyArray<readonly [number, number]>,
  unicodeVersion: string,
): void {
  if (unicodeVersion !== EXPECTED_UNICODE_VERSION) {
    throw new VisibleTextTableError(
      `table is Unicode ${unicodeVersion}, expected ${EXPECTED_UNICODE_VERSION}`,
    );
  }
  if (ranges.length === 0) {
    throw new VisibleTextTableError("blank range table is empty");
  }
  let previousEnd = -1;
  for (const [start, end] of ranges) {
    if (
      !Number.isInteger(start) ||
      !Number.isInteger(end) ||
      start < 0 ||
      end < start ||
      end > MAX_CODE_POINT ||
      start <= previousEnd
    ) {
      throw new VisibleTextTableError(
        `range (${start}, ${end}) is invalid, unsorted or overlapping`,
      );
    }
    previousEnd = end;
  }
}

assertValidBlankTable(BLANK_RANGES, VISIBLE_TEXT_UNICODE_VERSION);

/** Binary search over the sorted, non-overlapping ranges. */
function inBlankSet(codePoint: number): boolean {
  let low = 0;
  let high = BLANK_RANGES.length - 1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const [start, end] = BLANK_RANGES[middle];
    if (codePoint < start) {
      high = middle - 1;
    } else if (codePoint > end) {
      low = middle + 1;
    } else {
      return true;
    }
  }
  return false;
}

/**
 * True when `value` is non-empty and every code point in it shows the reader
 * nothing (D-378, RULE-INTEG-031 (a)).
 *
 * It iterates with `for...of`, so it tests CODE POINTS and not UTF-16 units: an
 * astral character such as U+1D159 (the null notehead) is one lookup rather
 * than two surrogate halves, and a LONE surrogate is yielded as itself, is not
 * in the table, and therefore makes the value non-blank - exactly as U+FFFD,
 * the replacement character an invalid byte sequence decodes to, does.
 *
 * The function never rewrites the value and never logs it.
 */
export function isBlankValue(value: string): boolean {
  if (value === "") return false;
  for (const character of value) {
    // `for...of` yields whole code points, so codePointAt(0) is never undefined.
    if (!inBlankSet(character.codePointAt(0) as number)) return false;
  }
  return true;
}
